/** Detalle de tarea con documento, expediente y todas las acciones del servidor. */
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { SectionCard } from '@/components/SectionCard';
import { ServerActionRunner } from '@/components/ServerActionRunner';
import { StatusBadge } from '@/components/StatusBadge';
import { DetailSkeleton } from '@/components/LoadingSkeleton';
import { hapticSuccess } from '@/utils/haptic';
import {
  useMobileTaskContextQuery,
  useMobileTaskMembershipsQuery,
  useMobileTaskQuery,
} from '@/hooks/useMobileTasks';
import { useMobileDocumentQuery, useQuotationExportMutation } from '@/hooks/useMobileQuotations';
import { useMobileWorkflowActions } from '@/hooks/useMobileWorkflowActions';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { useMonetaryPrecision } from '@/state/MonetaryPrecisionContext';
import { extractErrorMessage } from '@/services/mobileApiClient';
import {
  normalizeContextAction,
  type WorkflowContextAction,
  type WorkflowMembershipLookup,
} from '@/services/tasksService';
import { formatRelativeDate } from '@/utils/format';
import { workflowActionCode } from '@/utils/workflowActions';
import type {
  MobileBudgetDto,
  MobileQuotationDto,
  MobileRequirementDto,
} from '@/services/commercialService';

const ENTITY_LABELS: Record<string, string> = {
  budget: 'Presupuesto',
  quotation: 'Cotización',
  customer_requirement: 'Requerimiento',
};

function formatDate(value: string | undefined): string {
  if (!value) return '-';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function toDisplayNumber(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Une acciones de la tarea (prioritarias) con las del contexto del documento. */
function mergeActions(
  taskActions: WorkflowContextAction[],
  contextActions: WorkflowContextAction[],
): WorkflowContextAction[] {
  const byCode = new Map<string, WorkflowContextAction>();
  for (const action of contextActions) byCode.set(workflowActionCode(action), action);
  for (const action of taskActions) {
    const previous = byCode.get(workflowActionCode(action));
    byCode.set(workflowActionCode(action), { ...previous, ...action });
  }
  return [...byCode.values()].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
}

function withReassignmentOptions(
  action: WorkflowContextAction,
  memberships: WorkflowMembershipLookup[],
  pending: boolean,
  failed: boolean,
): WorkflowContextAction {
  if (workflowActionCode(action) !== 'reassign_task') return action;
  if (pending) {
    return { ...action, enabled: false, disabled_reason: 'Cargando responsables.' };
  }
  if (failed) {
    return {
      ...action,
      enabled: false,
      disabled_reason: 'No se pudieron cargar los responsables.',
    };
  }
  const eligible = memberships.filter((membership) => membership.eligible);
  if (eligible.length === 0) {
    return {
      ...action,
      enabled: false,
      disabled_reason: 'No hay responsables elegibles para reasignar.',
    };
  }
  return {
    ...action,
    data_fields: [
      ...(action.data_fields ?? []).filter((field) => field.name !== 'membership_id'),
      {
        name: 'membership_id',
        label: 'Nuevo responsable',
        required: true,
        options: eligible.map((membership) => ({
          value: membership.membership_id,
          label: membership.display_name,
        })),
      },
    ],
  };
}

export function TaskDetailPage() {
  const { taskId = '' } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useMobileAuth();
  const precision = useMonetaryPrecision();
  const detailQuery = useMobileTaskQuery(taskId);
  const detail = detailQuery.data;
  const task = detail?.task;
  const instanceSummary = detail?.instance_summary;
  const documentSummary = detail?.document_summary;
  const entityCode = instanceSummary?.entity_code;
  const entityId = instanceSummary?.entity_id;
  const contextQuery = useMobileTaskContextQuery(entityCode, entityId);
  const documentQuery = useMobileDocumentQuery(entityCode, entityId);
  const exportMutation = useQuotationExportMutation();
  const context = contextQuery.data;
  const activeInstance = (context?.latest_instances ?? []).find(
    (instance) => instance.status === 'pending' || instance.status === 'blocked',
  );
  const instanceForTask = (context?.latest_instances ?? []).find(
    (instance) => instance.id === task?.instance_id,
  );
  const controlInstance =
    (instanceForTask?.status === 'pending' || instanceForTask?.status === 'blocked'
      ? instanceForTask
      : undefined) ?? activeInstance;
  const taskActions = useMemo(
    () =>
      (detail?.available_actions ?? []).map((action) =>
        normalizeContextAction(action as unknown as Record<string, unknown>),
      ),
    [detail],
  );
  const contextActions = context?.actions ?? [];
  const hasReassignAction = taskActions.some(
    (action) => workflowActionCode(action) === 'reassign_task',
  );
  const membershipsQuery = useMobileTaskMembershipsQuery({
    entityCode,
    workflowPointCode: instanceSummary?.workflow_point_code,
    taskId: task?.id,
    enabled: hasReassignAction,
  });
  const mergedActions = useMemo(
    () =>
      mergeActions(taskActions, contextActions).map((action) =>
        withReassignmentOptions(
          action,
          membershipsQuery.data ?? [],
          membershipsQuery.isPending,
          membershipsQuery.isError,
        ),
      ),
    [
      taskActions,
      contextActions,
      membershipsQuery.data,
      membershipsQuery.isPending,
      membershipsQuery.isError,
    ],
  );
  const documentRevision = context?.document_revision ?? documentSummary?.document_revision;
  const workflowActions = useMobileWorkflowActions({
    entityCode: entityCode ?? '',
    entityId: entityId ?? '',
    actions: mergedActions,
    documentRevision,
    taskRevision: detail?.task_revision,
    activeInstanceId: controlInstance?.id,
    instanceRevision: controlInstance?.revision,
  });

  function handleActionSuccess() {
    hapticSuccess();
    toast.success('Acción ejecutada correctamente');
    navigate('/m/tasks', { replace: true });
  }

  async function handleExport() {
    if (!entityId) return;
    try {
      await exportMutation.mutateAsync(entityId);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }

  const document = documentQuery.data;

  if (detailQuery.isPending) {
    return (
      <MobileLayout title="Tarea" showBack>
        <DetailSkeleton />
      </MobileLayout>
    );
  }

  if (!detail || !task || !instanceSummary || !documentSummary) {
    return (
      <MobileLayout title="Tarea" showBack>
        <p className="p-8 text-center text-text-secondary">Tarea no encontrada.</p>
      </MobileLayout>
    );
  }

  const entityLabel =
    ENTITY_LABELS[instanceSummary.entity_code] ??
    instanceSummary.entity_code.replace(/_/g, ' ');
  const canExportQuotation =
    instanceSummary.entity_code === 'quotation' && hasPermission('quotations.export');
  const hasExpedienteContext =
    detail.route_version_number != null ||
    detail.definition_version_number != null ||
    !!detail.selected_rule_name;

  return (
    <MobileLayout
      title={entityLabel}
      subtitle={instanceSummary.workflow_point_code}
      showBack
    >
      <div className="space-y-4">
        <SectionCard
          title={entityLabel}
          action={
            <StatusBadge
              status={document?.status ?? documentSummary.business_status}
              size="sm"
            />
          }
        >
          <p className="break-words text-lg font-bold text-text-primary">
            {documentSummary.label}
          </p>

          {document && (
            <p title={document.id} className="truncate text-sm text-text-secondary">
              {entityCode === 'customer_requirement'
                ? (document as MobileRequirementDto).code ||
                  (document as MobileRequirementDto).title ||
                  document.id
                : entityCode === 'quotation'
                  ? (document as MobileQuotationDto).quotation_number ||
                    (document as MobileQuotationDto).code ||
                    document.id
                  : entityCode === 'budget'
                    ? (document as MobileBudgetDto).budget_code ||
                      (document as MobileBudgetDto).code ||
                      document.id
                    : document.id}
            </p>
          )}

          {document && entityCode === 'customer_requirement' && (
            <dl className="mt-2 divide-y divide-border-light text-sm">
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Código</dt>
                <dd
                  title={(document as MobileRequirementDto).code}
                  className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary"
                >
                  {(document as MobileRequirementDto).code || '-'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Cliente</dt>
                <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                  {(document as MobileRequirementDto).client?.legal_name || '-'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Fecha requerida</dt>
                <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                  {formatDate((document as MobileRequirementDto).required_date)}
                </dd>
              </div>
            </dl>
          )}
          {document &&
            entityCode === 'customer_requirement' &&
            (document as MobileRequirementDto).description && (
              <p className="mt-2 break-words border-t border-border-light pt-3 text-sm text-text-secondary">
                {(document as MobileRequirementDto).description}
              </p>
            )}

          {document && entityCode === 'quotation' && (
            <dl className="mt-2 divide-y divide-border-light text-sm">
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Número</dt>
                <dd
                  title={(document as MobileQuotationDto).quotation_number}
                  className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary"
                >
                  {(document as MobileQuotationDto).quotation_number ||
                    (document as MobileQuotationDto).code ||
                    '-'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Cliente</dt>
                <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                  {(document as MobileQuotationDto).project?.client_legal_name ||
                    (document as MobileQuotationDto).project?.name ||
                    '-'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Total</dt>
                <dd className="min-w-0 flex-1 truncate text-right font-medium tabular-nums text-text-primary">
                  {precision.format(
                    'quotations',
                    'total',
                    toDisplayNumber((document as MobileQuotationDto).total),
                    (document as MobileQuotationDto).currency ?? 'PEN',
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Vigencia</dt>
                <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                  {formatDate((document as MobileQuotationDto).valid_until)}
                </dd>
              </div>
            </dl>
          )}

          {document && entityCode === 'budget' && (
            <dl className="mt-2 divide-y divide-border-light text-sm">
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Código</dt>
                <dd
                  title={(document as MobileBudgetDto).budget_code}
                  className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary"
                >
                  {(document as MobileBudgetDto).budget_code ||
                    (document as MobileBudgetDto).code ||
                    '-'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Cliente</dt>
                <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                  {(document as MobileBudgetDto).customer_requirement?.client?.legal_name || '-'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Total</dt>
                <dd className="min-w-0 flex-1 truncate text-right font-medium tabular-nums text-text-primary">
                  {precision.format(
                    'budgets',
                    'sale_price',
                    toDisplayNumber(
                      (document as MobileBudgetDto).total ??
                        (document as MobileBudgetDto).sale_price,
                    ),
                    (document as MobileBudgetDto).currency ?? 'USD',
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">Estado</dt>
                <dd className="min-w-0 flex-1 truncate text-right text-text-secondary">
                  {document.status || '-'}
                </dd>
              </div>
            </dl>
          )}

          {canExportQuotation && entityId && (
            <MobileButton
              variant="secondary"
              size="sm"
              fullWidth
              className="mt-3"
              loading={exportMutation.isPending}
              onClick={() => void handleExport()}
              icon={<MobileIcon name="download" className="h-5 w-5" />}
            >
              Descargar documento
            </MobileButton>
          )}
        </SectionCard>

        {(context?.points ?? []).length > 0 && (
          <SectionCard title="Recorrido del workflow">
            <ol className="relative ml-3 space-y-3 border-l-2 border-border">
              {context!.points.map((point, idx) => (
                <li key={point.code} className="relative pl-4">
                  <span className="absolute -left-[11px] top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-primary bg-primary-light">
                    <MobileIcon
                      name={idx === 0 ? 'flag' : 'circle'}
                      className="h-3 w-3 text-primary"
                    />
                  </span>
                  <p className="break-words text-sm font-medium text-text-primary line-clamp-2">
                    {point.label}
                  </p>
                </li>
              ))}
            </ol>
          </SectionCard>
        )}

        {hasExpedienteContext && (
          <SectionCard title="Expediente">
            <dl className="divide-y divide-border-light text-sm">
              {detail.route_version_number != null && (
                <div className="flex items-center justify-between gap-3 py-2">
                  <dt className="shrink-0 text-text-tertiary">Recorrido</dt>
                  <dd className="min-w-0 flex-1 truncate text-right font-medium text-text-primary">
                    Versión {detail.route_version_number}
                  </dd>
                </div>
              )}
              {detail.definition_version_number != null && (
                <div className="flex items-center justify-between gap-3 py-2">
                  <dt className="shrink-0 text-text-tertiary">Definición</dt>
                  <dd className="min-w-0 flex-1 truncate text-right font-medium text-text-primary">
                    Versión {detail.definition_version_number}
                  </dd>
                </div>
              )}
              {detail.selected_rule_name && (
                <div className="flex items-center justify-between gap-3 py-2">
                  <dt className="shrink-0 text-text-tertiary">Regla aplicada</dt>
                  <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                    {detail.selected_rule_name}
                  </dd>
                </div>
              )}
            </dl>
          </SectionCard>
        )}

        <SectionCard title="Estado de la tarea">
          <div className="flex items-center justify-between gap-2">
            <StatusBadge status={task.status} />
            {task.due_at ? (
              <div className="shrink-0 text-right">
                <p className="text-xs uppercase tracking-wider text-text-tertiary">Vence</p>
                <p className="truncate text-sm font-medium text-text-primary">
                  {formatRelativeDate(task.due_at)}
                </p>
              </div>
            ) : (
              <div className="shrink-0 text-right">
                <p className="text-xs uppercase tracking-wider text-text-tertiary">Plazo</p>
                <p className="truncate text-sm font-medium text-text-primary">Sin plazo</p>
              </div>
            )}
          </div>
        </SectionCard>

        {mergedActions.length > 0 && (
          <SectionCard
            title="Acciones"
            subtitle="El servidor habilitó estas acciones para esta tarea."
          >
            <ServerActionRunner
              actions={mergedActions}
              documentRevision={documentRevision}
              reasonLabel="Motivo"
              onExecute={workflowActions.execute}
              onSuccess={handleActionSuccess}
              onConflictReload={() => {
                void detailQuery.refetch();
                void contextQuery.refetch();
                void membershipsQuery.refetch();
              }}
            />
          </SectionCard>
        )}
      </div>
    </MobileLayout>
  );
}
